import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, PlusCircle, Search, User, DollarSign, Loader2, CheckCircle2, Settings, History, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import PaymentReceipt from "./PaymentReceipt";
import ReceiptSettings from "./ReceiptSettings";
import TransactionHistory from "./TransactionHistory";
import { getCurrentTerm, type Term } from "@/utils/feeCalculations";

interface NewTransactionProps {
  schoolId: string | null;
  onBack: () => void;
}

const NewTransaction: React.FC<NewTransactionProps> = ({ schoolId, onBack }) => {
  const [regNumber, setRegNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [studentName, setStudentName] = useState<string | null>(null);
  const [studentClass, setStudentClass] = useState<string | null>(null);
  
  // States
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [completedTx, setCompletedTx] = useState<any>(null);
  const [activeTermLabel, setActiveTermLabel] = useState<string>("Current Term");
  const [activeTermKey, setActiveTermKey] = useState<string>("");
  const [isCashEnabled, setIsCashEnabled] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const { toast } = useToast();

  // Check if cash payments are enabled globally
  useEffect(() => {
    const checkCashConfig = async () => {
      if (!schoolId) {
        setLoadingConfig(false);
        return;
      }
      try {
        const docRef = doc(db, `schools/${schoolId}/systemConfig/paymentMethods/list/cash`);
        const snap = await getDoc(docRef);
        if (snap.exists() && snap.data().enabled === false) {
          setIsCashEnabled(false);
        } else {
          setIsCashEnabled(true);
        }
      } catch (err) {
        console.error("Failed to check cash config", err);
        setIsCashEnabled(true); // Default to true on error
      } finally {
        setLoadingConfig(false);
      }
    };
    checkCashConfig();
  }, [schoolId]);

  useEffect(() => {
    const fetchTerm = async () => {
      if (!schoolId) return;
      try {
        const termsSnap = await getDocs(collection(db, `schools/${schoolId}/terms`));
        const termsList = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Term));
        const term = getCurrentTerm(termsList);
        if (term) {
          // term.title from Firestore already includes the year e.g. "Term 1 2026"
          setActiveTermLabel(term.title || `Term ${term.number} ${new Date().getFullYear()}`);
          setActiveTermKey(term.id || "");
        }
      } catch (e) {
        console.error("Failed to fetch terms", e);
      }
    };
    fetchTerm();
  }, [schoolId]);

  const handleSearchStudent = async () => {
    if (!schoolId || !regNumber.trim()) return;
    setIsSearching(true);
    setStudentName(null);

    try {
      const q = query(
        collection(db, `schools/${schoolId}/students`),
        where("registrationNumber", "==", regNumber.trim())
      );
      const snap = await getDocs(q);

      if (snap.empty) {
        toast({
          title: "Student Not Found",
          description: `No student found with registration number ${regNumber}`,
          variant: "destructive"
        });
        setStudentClass(null);
      } else {
        const studentData = snap.docs[0].data();
        const fullName = `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim() || studentData.fullName || "Unknown Name";
        setStudentName(fullName);
        setStudentClass(studentData.className || "Unknown Class");
      }
    } catch (err) {
      toast({
        title: "Search Failed",
        description: "An error occurred while looking up the student.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleReviewPayment = () => {
    if (!studentName) {
      toast({ title: "Verification Required", description: "Please search and verify the student first.", variant: "destructive"});
      return;
    }
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast({ title: "Invalid Amount", description: "Please enter a valid amount greater than 0.", variant: "destructive"});
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmPayment = async () => {
    if (!schoolId || !amount || !studentName) return;
    setIsProcessing(true);

    try {
      const colRef = collection(db, `schools/${schoolId}/transactions/cash/records`);
      
      const safeAmount = Number(amount);

      await addDoc(colRef, {
        studentName: studentName,
        amount: safeAmount,
        provider: "cash",
        status: "completed",
        transactionDate: serverTimestamp(),
        reference: `CASH-${Math.floor(Date.now() / 1000)}`,
        accountReference: `Fee-${regNumber.trim()}`,
        termLabel: activeTermLabel,
        termKey: activeTermKey,
        createdBy: "admin-ui" // Tracking origin
      });

      setCompletedTx({
        studentName: studentName,
        regNumber: regNumber.trim(),
        className: studentClass || "Unknown",
        termLabel: activeTermLabel,
        amount: safeAmount,
        reference: `CASH-${Math.floor(Date.now() / 1000)}`,
        date: new Date(),
        method: "Cash"
      });

      setShowConfirm(false);
      setShowSuccess(true);
      
    } catch (err) {
      console.error(err);
      toast({
        title: "Transaction Failed",
        description: "An error occurred while saving the payment.",
        variant: "destructive"
      });
      setShowConfirm(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setRegNumber("");
    setAmount("");
    setStudentName(null);
    setStudentClass(null);
    setShowSuccess(false);
    setShowReceipt(false);
    setCompletedTx(null);
  };

  if (showSuccess) {
    return (
      <div className="flex flex-col h-full bg-transparent p-4 sm:p-6 items-center overflow-y-auto">
        <div className="max-w-md w-full text-center space-y-6 bg-card border rounded-xl p-8 shadow-sm my-auto">
          <div className="mx-auto w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Payment Successful!</h2>
            <p className="text-muted-foreground mt-2">
              Successfully recorded <span className="font-semibold text-foreground">KES {Number(amount).toLocaleString()}</span> for <br/> <span className="font-semibold text-foreground">{studentName} ({regNumber})</span>.
            </p>
          </div>
          
          {showReceipt && completedTx ? (
            <div className="mt-8 border-t pt-8 w-full flex justify-center fade-in duration-300">
               <PaymentReceipt transactionInfo={completedTx} schoolId={schoolId} onClose={() => setShowReceipt(false)} />
            </div>
          ) : (
            <div className="pt-4 flex flex-col gap-3">
              <Button 
                onClick={() => setShowReceipt(true)} 
                className="w-full bg-slate-900 hover:bg-slate-800 text-white shadow-md font-semibold"
              >
                Issue Receipt
              </Button>
              <Button onClick={resetForm} variant="outline" className="w-full">
                Record Another Payment
              </Button>
              <Button variant="ghost" onClick={onBack} className="w-full">
                Return to Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (showSettings) {
    return <ReceiptSettings schoolId={schoolId} onBack={() => setShowSettings(false)} />;
  }

  if (showHistory) {
    return <TransactionHistory schoolId={schoolId} onBack={() => setShowHistory(false)} />;
  }

  return (
    <div className="flex flex-col h-full bg-transparent text-foreground overflow-y-auto p-4 sm:p-6">
      {/* Header Matching Finance Dashboard */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <Button 
          variant="outline"
          size="sm"
          onClick={onBack}
          className="gap-2 w-fit border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </Button>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <PlusCircle className="h-6 w-6 text-blue-600 dark:text-blue-500" />
            New Cash Transaction
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Register a direct physical cash fee payment over the counter.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => setShowHistory(true)} className="gap-2">
            <History className="h-4 w-4" />
            Transactions
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} className="gap-2">
            <Settings className="h-4 w-4" />
            Receipt Designs
          </Button>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 max-w-2xl w-full mx-auto space-y-6 pb-20">
        {!isCashEnabled ? (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 flex flex-col items-center text-center">
            <ShieldAlert className="h-12 w-12 text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold text-amber-900 dark:text-amber-100 mb-2">Cash Payments Disabled</h2>
            <p className="text-amber-700 dark:text-amber-300">
              The school administrator has disabled over-the-counter cash payments. Please use alternative payment methods.
            </p>
            <Button onClick={onBack} className="mt-6 bg-amber-600 hover:bg-amber-700 text-white">
              Return to Dashboard
            </Button>
          </div>
        ) : (
          <Card className="shadow-sm border-gray-200 dark:border-gray-800">
            <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-900/50 pb-5">
            <CardTitle className="text-lg">Payment Details</CardTitle>
            <CardDescription>
              First lookup the student using their Registration Number, then enter the cash amount received.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-8">
            
            {/* Step 1: Student Lookup */}
            <div className="space-y-4">
              <Label className="text-sm font-semibold flex items-center gap-2 text-foreground">
                <User className="h-4 w-4 text-blue-500" />
                Step 1: Student Registration Number
              </Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Input 
                    placeholder="e.g. ADM1001" 
                    value={regNumber}
                    onChange={(e) => {
                      setRegNumber(e.target.value.toUpperCase());
                      setStudentName(null); // Reset verified name if they type again
                    }}
                    className="pl-4 h-11"
                    disabled={isSearching}
                  />
                </div>
                <Button 
                  onClick={handleSearchStudent} 
                  disabled={!regNumber.trim() || isSearching}
                  className="h-11 w-32 shrink-0 bg-secondary text-secondary-foreground hover:bg-secondary/80"
                >
                  {isSearching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                  Verify
                </Button>
              </div>

              {/* Verification Result Banner */}
              {studentName && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-start gap-3 fade-in zoom-in duration-200">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-semibold text-green-900 dark:text-green-300">Student Verified</h4>
                    <p className="text-green-800 dark:text-green-400 text-sm mt-1">
                      <strong>{studentName}</strong> • {studentClass}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Amount Entry */}
            <div className={`space-y-4 transition-opacity duration-300 ${!studentName ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
              <div className="h-px w-full bg-border my-6"></div>
              <Label className="text-sm font-semibold flex items-center gap-2 text-foreground">
                <DollarSign className="h-4 w-4 text-green-500" />
                Step 2: Enter Amount Received
              </Label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <span className="text-muted-foreground font-medium">KES</span>
                </div>
                <Input 
                  type="number"
                  placeholder="0.00" 
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-14 h-14 text-2xl font-semibold placeholder:font-normal placeholder:text-muted-foreground/50"
                  disabled={!studentName}
                />
              </div>
            </div>

          </CardContent>
          <CardFooter className="bg-gray-50 dark:bg-gray-900/50 border-t p-6 pb-6">
            <Button 
              className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-md hover:shadow-lg"
              disabled={!studentName || !amount || Number(amount) <= 0}
              onClick={handleReviewPayment}
            >
              Review Payment
            </Button>
          </CardFooter>
        </Card>
        )}
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent className="sm:max-w-md bg-white dark:bg-gray-900 border-0 shadow-2xl rounded-xl overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-blue-500 to-green-500"></div>
          
          <AlertDialogHeader className="pt-4 pb-2">
            <AlertDialogTitle className="text-xl font-bold text-center">Confirm Cash Receipt</AlertDialogTitle>
            <AlertDialogDescription className="text-center pt-2 text-base text-foreground">
              Please count the physical cash carefully.
              You are about to permanently register a payment of:
            </AlertDialogDescription>
            <div className="bg-gray-50 dark:bg-gray-800/80 rounded-lg p-5 mt-4 border border-border shadow-inner">
              <div className="flex justify-between items-center mb-2 pb-2 border-b border-border text-sm">
                <span className="text-muted-foreground">Student</span>
                <span className="font-semibold text-right">{studentName} <br/> <span className="text-xs text-muted-foreground">({regNumber})</span></span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-muted-foreground">Amount</span>
                <span className="text-2xl font-bold text-green-600 dark:text-green-500">KES {Number(amount).toLocaleString()}</span>
              </div>
            </div>
          </AlertDialogHeader>
          
          <AlertDialogFooter className="sm:justify-center gap-3 mt-6 pb-2">
            <AlertDialogCancel 
              disabled={isProcessing}
              className="mt-0 sm:mt-0 px-6 font-medium rounded-lg border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => { e.preventDefault(); handleConfirmPayment(); }}
              disabled={isProcessing}
              className="px-6 font-medium rounded-lg shadow-sm border-0 transition-all bg-green-600 hover:bg-green-700 text-white"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                "Confirm & Save"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default NewTransaction;
